import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { WantToGoButton } from '#app/components/want-to-go-button.tsx'
import {
	getSpecialFilter,
	FEATURED_SPECIAL_FILTERS,
} from '#app/data/near-me-filters.ts'
import {
	getScoreContext,
	getScoreExplainer,
	getScoreSupportText,
} from '#app/utils/consensus.shared.ts'
import { getTopCuisinesForChips } from '#app/utils/cuisine-utils.server.ts'
import { getDomainUrl } from '#app/utils/misc.tsx'
import { useGeolocation } from '#app/utils/use-geolocation.ts'
import { type Route } from './+types/london.near-me.tsx'

export const meta: Route.MetaFunction = ({ data }) => {
	const year = new Date().getFullYear()
	const title = `Restaurants Near Me in London (${year}) | What to Order First`
	const description =
		'Use your location to find good food nearby fast, with the best thing to order first at each place.'

	return [
		{ title },
		{ name: 'description', content: description },
		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:type', content: 'website' },
		{ property: 'og:url', content: data?.canonical },
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ rel: 'canonical', href: data?.canonical },
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const canonical = `${getDomainUrl(request)}/london/near-me`
	const topCuisines = await getTopCuisinesForChips(8)
	return { canonical, topCuisines }
}

type NearbyRestaurant = {
	id: string
	name: string
	slug: string
	cuisine: string | null
	priceRange: string | null
	consensusScore: number | null
	summaryOneLiner: string | null
	visited: boolean
	isHiddenGem: boolean
	isCultClassic: boolean
	safeFirstOrder: string | null
	safeFirstTip: string | null
	latitude: number
	longitude: number
	address: string | null
	mapUrl: string
	distance_km: number
}

type Coordinates = {
	lat: number
	lng: number
}

export default function NearMePage({ loaderData }: Route.ComponentProps) {
	const { canonical, topCuisines } = loaderData
	const {
		coordinates: geoCoords,
		error: geoError,
		loading: geoLoading,
		requestLocation,
	} = useGeolocation()

	const [postcode, setPostcode] = useState('')
	const [postcodeError, setPostcodeError] = useState<string | null>(null)
	const [postcodeLoading, setPostcodeLoading] = useState(false)
	const [searchCoords, setSearchCoords] = useState<Coordinates | null>(null)
	const [searchLocation, setSearchLocation] = useState<string | null>(null)

	const [restaurants, setRestaurants] = useState<NearbyRestaurant[]>([])
	const [fetching, setFetching] = useState(false)
	const [fetchError, setFetchError] = useState<string | null>(null)
	const [hasSearched, setHasSearched] = useState(false)

	const fetchNearbyRestaurants = async (coords: Coordinates) => {
		setFetching(true)
		setFetchError(null)
		setHasSearched(true)
		try {
			const params = new URLSearchParams({
				lat: coords.lat.toString(),
				lng: coords.lng.toString(),
				radius: '5',
				limit: '30',
			})
			const res = await fetch(`/resources/nearby-restaurants?${params}`)
			const data = (await res.json()) as {
				error: string | null
				restaurants: NearbyRestaurant[]
			}
			if (data.error) {
				setFetchError(data.error)
			} else {
				setRestaurants(data.restaurants)
			}
		} catch {
			setFetchError('Failed to fetch nearby restaurants')
		} finally {
			setFetching(false)
		}
	}

	// Auto-request location on mount
	useEffect(() => {
		requestLocation()
		setSearchLocation('your location')
	}, [requestLocation])

	// When geolocation succeeds, fetch restaurants
	useEffect(() => {
		if (geoCoords && !searchCoords && !fetching && !hasSearched) {
			setSearchCoords(geoCoords)
			void fetchNearbyRestaurants(geoCoords)
		}
	}, [geoCoords, searchCoords, fetching, hasSearched])

	const handlePostcodeSearch = async (e: React.FormEvent) => {
		e.preventDefault()
		const trimmedPostcode = postcode.trim().toUpperCase()

		if (!trimmedPostcode) {
			setPostcodeError('Please enter a postcode')
			return
		}

		setPostcodeLoading(true)
		setPostcodeError(null)

		try {
			// Use postcodes.io free API for UK postcode geocoding
			const res = await fetch(
				`https://api.postcodes.io/postcodes/${encodeURIComponent(trimmedPostcode)}`,
			)
			const data = (await res.json()) as {
				status: number
				result?: {
					latitude: number
					longitude: number
					admin_district?: string
				}
				error?: string
			}

			if (data.status !== 200 || !data.result) {
				setPostcodeError('Invalid postcode. Please check and try again.')
				return
			}

			const coords = {
				lat: data.result.latitude,
				lng: data.result.longitude,
			}
			setSearchCoords(coords)
			setSearchLocation(trimmedPostcode)
			await fetchNearbyRestaurants(coords)
		} catch {
			setPostcodeError('Failed to look up postcode. Please try again.')
		} finally {
			setPostcodeLoading(false)
		}
	}

	const isLoading = geoLoading || postcodeLoading || fetching
	// Only show postcode fallback if location was denied
	const showPostcodeFallback = geoError && !hasSearched && !isLoading
	const canonicalOrigin = (() => {
		try {
			return new URL(canonical).origin
		} catch {
			return ''
		}
	})()
	const ldBreadcrumb = {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: [
			{
				'@type': 'ListItem',
				position: 1,
				name: 'Home',
				item: `${canonicalOrigin}/`,
			},
			{
				'@type': 'ListItem',
				position: 2,
				name: 'London',
				item: `${canonicalOrigin}/london`,
			},
			{
				'@type': 'ListItem',
				position: 3,
				name: 'Near Me',
				item: canonical,
			},
		],
	}

	return (
		<main className="min-h-screen bg-[#05050a] text-[#f8f8ff]">
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(ldBreadcrumb) }}
			/>
			{/* Hero Section */}
			<section className="border-b border-[#1a1a2e] bg-gradient-to-b from-[#000000] via-[#05050a] to-[#0a001f]">
				<div className="container mx-auto px-6 py-12 lg:py-16">
					{/* Breadcrumb */}
					<nav className="mb-6 flex items-center gap-2 text-sm text-[#b0b0e0]">
						<Link to="/" className="hover:text-[#422ad5]">
							Home
						</Link>
						<span>/</span>
						<Link to="/london" className="hover:text-[#422ad5]">
							London
						</Link>
						<span>/</span>
						<span className="text-[#f8f8ff]">Near Me</span>
					</nav>

					<div className="max-w-3xl space-y-4">
						<h1 className="text-4xl font-black tracking-tight text-[#f8f8ff] sm:text-5xl">
							Restaurants Near You
						</h1>
						<p className="text-lg text-[#b0b0e0]">
							{isLoading
								? 'Finding restaurants near your location...'
								: restaurants.length > 0
									? `Found ${restaurants.length} restaurants within 4km${searchLocation ? ` of ${searchLocation}` : ''}`
									: 'Find good food nearby and know what to order first'}
						</p>
						<p className="max-w-2xl text-sm text-[#7070a0]">
							Every place here is handpicked. Signal score shows how strong and
							consistent the review coverage is, not whether a place is "good"
							or "bad".
						</p>
					</div>
				</div>
			</section>

			{/* Filter Chips */}
			<section className="border-b border-[#1a1a2e]">
				<div className="container mx-auto px-6 py-4">
					<div className="scrollbar-hide -mb-2 flex items-center gap-2 overflow-x-auto pb-2">
						<span className="btn btn-sm border-[#422ad5] bg-[#422ad5] whitespace-nowrap text-white">
							All Near Me
						</span>
						{/* Special filters (price, quality) */}
						{FEATURED_SPECIAL_FILTERS.map((filterSlug) => {
							const filterData = getSpecialFilter(filterSlug)
							if (!filterData) return null
							return (
								<Link
									key={filterSlug}
									to={`/london/near-me/${filterSlug}`}
									className="btn btn-sm border-[#1a1a2e] bg-[#1a1a2e] whitespace-nowrap text-[#b0b0e0] hover:bg-[#1a1a2e]/80"
								>
									{filterData.title.replace(' Near You', '')}
								</Link>
							)
						})}
						{/* Dynamic cuisines from database */}
						{topCuisines.map((cuisine) => (
							<Link
								key={cuisine.slug}
								to={`/london/near-me/${cuisine.slug}`}
								className="btn btn-sm border-[#1a1a2e] bg-[#1a1a2e] whitespace-nowrap text-[#b0b0e0] hover:bg-[#1a1a2e]/80"
							>
								{cuisine.cuisine}
							</Link>
						))}
					</div>
				</div>
			</section>

			{/* Content Section */}
			<section className="container mx-auto px-6 py-12">
				{/* Location denied - show postcode fallback */}
				{showPostcodeFallback && (
					<div className="mx-auto max-w-lg">
						<div className="space-y-6 rounded-lg border border-[#1a1a2e] bg-[#0d0d15] p-8">
							<div className="space-y-2 text-center">
								<h2 className="text-xl font-bold text-[#f8f8ff]">
									Location Access Needed
								</h2>
								<p className="text-sm text-[#b0b0e0]">
									{geoError?.code === 1
										? "You've denied location access. Enter your postcode instead to find restaurants near you."
										: 'Unable to get your location. Enter your postcode instead.'}
								</p>
							</div>

							{/* Postcode search */}
							<form onSubmit={handlePostcodeSearch} className="space-y-4">
								<div>
									<input
										type="text"
										id="postcode"
										value={postcode}
										onChange={(e) => setPostcode(e.target.value)}
										placeholder="Enter your postcode (e.g. SW1A 1AA)"
										className="w-full rounded-lg border border-[#1a1a2e] bg-[#151525] px-4 py-3 text-[#f8f8ff] placeholder-[#7070a0] focus:border-[#422ad5] focus:outline-none"
									/>
									{postcodeError && (
										<p className="mt-2 text-sm text-[#ff2e63]">
											{postcodeError}
										</p>
									)}
								</div>
								<button
									type="submit"
									disabled={postcodeLoading}
									className="btn w-full border-none bg-[#422ad5] text-white hover:bg-[#422ad5]/80"
								>
									{postcodeLoading ? (
										<span className="loading loading-spinner loading-sm" />
									) : (
										'Find Restaurants'
									)}
								</button>
							</form>
						</div>
					</div>
				)}

				{/* Loading state - show while getting location or fetching */}
				{isLoading && (
					<div className="flex flex-col items-center justify-center gap-4 py-20">
						<span className="loading loading-spinner loading-lg text-[#00e0ff]" />
						<p className="text-[#b0b0e0]">
							{geoLoading
								? 'Getting your location...'
								: 'Finding nearby restaurants...'}
						</p>
					</div>
				)}

				{/* Fetch error */}
				{fetchError && !isLoading && (
					<div className="mx-auto max-w-lg py-12 text-center">
						<div className="rounded-lg border border-[#ff2e63] bg-[#0d0d15] p-8">
							<h2 className="mb-4 text-xl font-bold text-[#ff2e63]">
								Something went wrong
							</h2>
							<p className="mb-6 text-[#b0b0e0]">{fetchError}</p>
							<button
								onClick={() => setHasSearched(false)}
								className="btn border border-[#ff2e63] bg-[#ff2e63]/10 text-[#ff2e63] hover:bg-[#ff2e63]/20"
							>
								Try Again
							</button>
						</div>
					</div>
				)}

				{/* No results */}
				{!isLoading &&
					!fetchError &&
					restaurants.length === 0 &&
					hasSearched && (
						<div className="mx-auto max-w-lg py-12 text-center">
							<div className="rounded-lg border border-[#1a1a2e] bg-[#0d0d15] p-8">
								<h2 className="mb-4 text-xl font-bold text-[#f8f8ff]">
									No restaurants found nearby
								</h2>
								<p className="mb-6 text-[#b0b0e0]">
									We couldn't find any restaurants within 4km
									{searchLocation ? ` of ${searchLocation}` : ''}. Try a
									different location or browse by area.
								</p>
								<div className="flex flex-col justify-center gap-3 sm:flex-row">
									<button
										onClick={() => setHasSearched(false)}
										className="btn border border-[#00e0ff] bg-[#00e0ff]/10 text-[#00e0ff] hover:bg-[#00e0ff]/20"
									>
										Try Different Location
									</button>
									<Link
										to="/london"
										className="btn border border-[#422ad5] bg-[#422ad5]/10 text-[#f8f8ff] hover:bg-[#422ad5]/20"
									>
										Browse by Area
									</Link>
								</div>
							</div>
						</div>
					)}

				{/* Restaurant results */}
				{!isLoading && restaurants.length > 0 && (
					<>
						{/* Search again button */}
						<div className="mb-6 flex items-center justify-between">
							<p className="text-[#b0b0e0]">
								Showing restaurants near{' '}
								<span className="text-[#00e0ff]">{searchLocation}</span>
							</p>
							<button
								onClick={() => {
									setHasSearched(false)
									setRestaurants([])
									setSearchCoords(null)
								}}
								className="btn btn-sm border border-[#1a1a2e] bg-[#1a1a2e] text-[#b0b0e0] hover:bg-[#1a1a2e]/80"
							>
								Change Location
							</button>
						</div>

						<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
							{restaurants.map((restaurant, index) => (
								<NearbyRestaurantCard
									key={restaurant.id}
									restaurant={restaurant}
									rank={index + 1}
								/>
							))}
						</div>
					</>
				)}
			</section>
		</main>
	)
}

function NearbyRestaurantCard({
	restaurant,
	rank,
}: {
	restaurant: NearbyRestaurant
	rank: number
}) {
	const scoreContext = getScoreContext(restaurant.consensusScore ?? 0)
	const distanceDisplay =
		restaurant.distance_km < 1
			? `${Math.round(restaurant.distance_km * 1000)}m away`
			: `${restaurant.distance_km.toFixed(1)}km away`

	return (
		<article className="card border border-[#1a1a2e] bg-[#0d0d15] shadow-[0_0_20px_rgba(66,42,213,0.12)] transition hover:shadow-[0_0_28px_rgba(0,224,255,0.2)]">
			<div className="card-body space-y-4 p-5">
				{/* Header with name and score */}
				<div className="flex items-start justify-between gap-3">
					<div className="flex items-center gap-3">
						<span className="badge badge-lg border border-[#422ad5] font-bold text-[#422ad5]">
							{rank}
						</span>
						<div>
							<Link
								to={`/london/restaurants/${restaurant.slug}`}
								className="card-title text-[#f8f8ff] hover:text-[#00e0ff]"
							>
								{restaurant.name}
							</Link>
							<p className="mt-1 text-xs tracking-[0.1em] text-[#b0b0e0] uppercase">
								{restaurant.cuisine} · {restaurant.priceRange}
							</p>
						</div>
					</div>
					{restaurant.consensusScore !== null && (
						<span
							className="badge badge-sm gap-1 font-semibold"
							style={{
								borderColor: scoreContext.color,
								color: scoreContext.color,
								backgroundColor: `${scoreContext.color}1a`,
							}}
							title={getScoreExplainer(restaurant.consensusScore)}
						>
							{restaurant.consensusScore}
							<span className="text-[10px] tracking-[0.18em] text-[#b0b0e0] uppercase">
								Signal
							</span>
						</span>
					)}
				</div>

				{/* Distance badge */}
				<div className="flex flex-wrap gap-2">
					<span className="badge badge-sm border border-[#00e0ff] bg-[#00e0ff]/10 px-3 py-1 text-[#00e0ff]">
						{distanceDisplay}
					</span>
					{restaurant.visited && (
						<span className="badge badge-sm border border-[#00e0ff] tracking-[0.1em] text-[#00e0ff] uppercase">
							✓ Visited
						</span>
					)}
					{restaurant.isHiddenGem && (
						<span className="badge badge-sm border border-[#00e0ff] tracking-[0.1em] text-[#00e0ff] uppercase">
							Hidden Gem
						</span>
					)}
					{restaurant.isCultClassic && (
						<span className="badge badge-sm border border-[#ff9f1c] tracking-[0.1em] text-[#ff9f1c] uppercase">
							Cult Classic
						</span>
					)}
					{(restaurant.consensusScore ?? 0) < 45 && !restaurant.isHiddenGem && (
						<span className="badge badge-sm border border-[#7070a0] tracking-[0.1em] text-[#b0b0e0] uppercase">
							Light Coverage
						</span>
					)}
				</div>

				{restaurant.safeFirstOrder && (
					<div className="rounded-lg border border-[#1a1a2e] bg-[#151525] p-3">
						<p className="mb-1 text-xs tracking-wide text-[#b0b0e0] uppercase">
							Start with
						</p>
						<p className="font-semibold text-[#f8f8ff]">
							{restaurant.safeFirstOrder}
						</p>
						{restaurant.safeFirstTip && (
							<p className="mt-1 text-sm text-[#b0b0e0]">
								{restaurant.safeFirstTip}
							</p>
						)}
					</div>
				)}

				{/* Summary */}
				{restaurant.summaryOneLiner && (
					<p className="line-clamp-2 text-sm text-[#b0b0e0]">
						{restaurant.summaryOneLiner}
					</p>
				)}

				<p className="text-xs text-[#7070a0]">
					{getScoreSupportText(restaurant.consensusScore ?? 0)}
				</p>

				{/* Address */}
				{restaurant.address && (
					<p className="text-xs text-[#7070a0]">{restaurant.address}</p>
				)}

				{/* Actions */}
				<div className="card-actions items-center justify-between pt-2">
					{restaurant.mapUrl && (
						<a
							href={restaurant.mapUrl}
							target="_blank"
							rel="noopener noreferrer"
							className="btn btn-ghost btn-sm gap-1 text-[#f8f8ff] hover:text-[#00e0ff]"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth={1.5}
								stroke="currentColor"
								className="size-4"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
								/>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
								/>
							</svg>
							Directions
						</a>
					)}
					<div className="flex items-center gap-2">
						<WantToGoButton restaurantId={restaurant.id} size="sm" />
						<Link
							to={`/london/restaurants/${restaurant.slug}`}
							className="btn btn-sm border-none bg-[#422ad5] text-white hover:bg-[#422ad5]/80"
						>
							What to Order
						</Link>
					</div>
				</div>
			</div>
		</article>
	)
}
